import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, FormControlLabel, Grid2 as Grid, Grid2, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useAccount } from 'wagmi';

import { REVERSE_PROXY_IP } from 'src/global/config/common.config';
import { colors } from 'src/global/styles/colors';
import { ProjectInputs } from 'src/global/types/project.type';
import { useValidateDomainMutation } from 'src/store/services/project.service';

import { AdminCheckbox } from '../AdminCheckbox';
import { AdminInput } from '../AdminTextField';
import { StepAccordion } from '../StepAccordion';
import { FAQs } from './FAQs';
import { SocialNetworks } from './SocialNetworks';

interface GeneralProjectInfoProps {
  isDisabled?: boolean;
}

const urlGoggleSupport =
  'https://support.google.com/analytics/topic/14088998?hl=en&ref_topic=14090456&sjid=1192053321497635489-NC';

const GeneralProjectInfo = ({ isDisabled }: GeneralProjectInfoProps) => {
  const { address } = useAccount();
  const [isOpenDnsConfig, setIsOpenDnsConfig] = useState(false);
  const [isSuccessDnsConfig, setIsSuccessDnsConfig] = useState(false);
  const [previousCustomDomainState, setPreviousCustomDomainState] = useState(false);
  const {
    control,
    watch,
    formState: { errors },
    setError,
    clearErrors,
    setValue,
  } = useFormContext<ProjectInputs>();
  const nodeSaleUrl = watch('info.nodeSaleUrl');
  const isCustomDomain = watch('info.isCustomDomain');

  const [validateDomain] = useValidateDomainMutation();

  useEffect(() => {
    if (previousCustomDomainState && !isCustomDomain) {
      setValue('info.nodeSaleUrl', '');
      setIsSuccessDnsConfig(false);
      clearErrors('info.nodeSaleUrl');
    }
    setPreviousCustomDomainState(isCustomDomain || false);
  }, [isCustomDomain, previousCustomDomainState, setValue]);

  return (
    <StepAccordion
      isError={!!errors.name || !!errors.info || !!errors.faqs}
      index={1}
      title='General Project Information'
      open>
      <Typography variant='body1' fontSize={'14px'} fontWeight={400} sx={{ color: 'white' }} paddingBottom={3}>
        Provide key details about your project such as website, social media links, whitepaper and analytics. This
        information helps build trust and transparency with your users. Visit the{' '}
        <a
          href={'https://link.lfg.inc/kb-coresale-generalprojectinfo'}
          target={'_blank'}
          style={{ color: 'white', textDecoration: 'underline' }}>
          General Project Information
        </a>{' '}
        article to learn more.
      </Typography>
      <Grid container columnSpacing={2}>
        <Grid size={6} mb={3}>
          <Box width={'100%'}>
            <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
              Project Name
            </Typography>
            <Controller
              name='name'
              control={control}
              render={({ field }) => (
                <AdminInput
                  readOnly={isDisabled}
                  isError={!!errors['name']}
                  errorMsg={errors['name']?.message}
                  placeholder={'Enter name'}
                  {...field}
                />
              )}
            />
          </Box>
        </Grid>
        <Grid size={6} mb={3}>
          <Box width={'100%'}>
            <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
              Website
            </Typography>
            <Controller
              name='info.url'
              control={control}
              render={({ field }) => (
                <AdminInput
                  readOnly={isDisabled}
                  isError={!!errors?.info?.url}
                  errorMsg={errors.info?.url?.message}
                  placeholder={'Paste URL'}
                  {...field}
                />
              )}
            />
          </Box>
        </Grid>
        <Grid size={12} mb={3}>
          <Box>
            <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
              DNS Configuration
            </Typography>
            <Controller
              control={control}
              name={`info.isCustomDomain`}
              defaultValue={false}
              render={({ field: { value, onChange } }) => (
                <FormControlLabel
                  label={<Typography color='white'>Custom project's domain</Typography>}
                  control={<AdminCheckbox checked={value} disabled={isDisabled} onChange={onChange} />}
                />
              )}
            />
          </Box>
          {isCustomDomain && (
            <Box width={'100%'} mt={1}>
              <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
                Production Core Sale URL
              </Typography>
              <Controller
                name='info.nodeSaleUrl'
                control={control}
                render={({ field }) => (
                  <AdminInput
                    readOnly={isDisabled}
                    isError={!!errors?.info?.nodeSaleUrl}
                    errorMsg={errors.info?.nodeSaleUrl?.message}
                    isSuccess={isSuccessDnsConfig}
                    successMsg={'Domain name is valid and available for use.'}
                    placeholder={'E.g., node.lfg.inc'}
                    {...field}
                  />
                )}
              />
            </Box>
          )}
        </Grid>
        {isCustomDomain && (
          <Grid size={12} mb={3}>
            <Box width={'100%'}>
              <Stack
                direction='row'
                alignItems='center'
                spacing={0.5}
                sx={{
                  opacity: isOpenDnsConfig ? 1 : 0.3,
                }}>
                <Typography color={'white'} fontSize={'14px'} variant='h6'>
                  DNS Configuration
                </Typography>
                <KeyboardArrowDownIcon
                  className={`${isOpenDnsConfig ? 'rotate-180' : ''}`}
                  onClick={() => setIsOpenDnsConfig(!isOpenDnsConfig)}
                  sx={{
                    cursor: 'pointer',
                    color: 'white',
                  }}
                />
              </Stack>
              {isOpenDnsConfig && (
                <Grid2 container spacing={2} paddingTop={2}>
                  <Grid2 size={12}>
                    <Typography color='white' fontSize={'14px'}>
                      Add these information to your DNS records to setup the reverse proxy. A successful setup displays
                      '
                      <span
                        style={{
                          fontWeight: '500',
                        }}>
                        Reverse proxy configured successfully
                      </span>
                      ' when accessing the Production URL.
                    </Typography>
                  </Grid2>
                  <Grid2 size={2}>
                    <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
                      Type
                    </Typography>
                    <Typography paddingBottom={1} color={'white'} fontSize={'14px'} variant='h6'>
                      CNAME
                    </Typography>
                  </Grid2>
                  <Grid2 size={5}>
                    <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
                      Host Name
                    </Typography>
                    <Typography paddingBottom={1} color={'white'} fontSize={'14px'} variant='h6'>
                      {watch('info.nodeSaleUrl')}
                    </Typography>
                  </Grid2>{' '}
                  <Grid2 size={5}>
                    <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
                      Value
                    </Typography>
                    <Typography paddingBottom={1} color={'white'} fontSize={'14px'} variant='h6'>
                      {REVERSE_PROXY_IP}
                    </Typography>
                  </Grid2>
                </Grid2>
              )}
            </Box>
          </Grid>
        )}
        <Grid size={12} mb={3}>
          <Box width={'100%'}>
            <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
              Project Description
            </Typography>
            <Controller
              name='info.description'
              control={control}
              render={({ field }) => (
                <AdminInput
                  readOnly={isDisabled}
                  rows={5}
                  inputHeight='140px'
                  multiline
                  inputPd={'14px 0 14px 16px'}
                  isError={!!errors?.info?.description}
                  errorMsg={errors.info?.description?.message}
                  placeholder={'Give a short description about the project'}
                  sx={{
                    '& .MuiFilledInput-root': {
                      padding: 0,
                    },
                  }}
                  {...field}
                />
              )}
            />
          </Box>
        </Grid>
        <Grid size={12} mb={3}>
          <Box width={'100%'}>
            <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
              Whitepaper URL
            </Typography>
            <Controller
              name='info.whitepaperUrl'
              control={control}
              render={({ field }) => (
                <AdminInput
                  readOnly={isDisabled}
                  rows={4}
                  isError={!!errors?.info?.whitepaperUrl}
                  errorMsg={errors.info?.whitepaperUrl?.message}
                  placeholder={'Paste URL'}
                  {...field}
                />
              )}
            />
          </Box>
        </Grid>
        <Grid size={6} mb={3}>
          <Box width={'100%'}>
            <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
              Project Terms of Service
            </Typography>
            <Controller
              name='info.termOfService'
              control={control}
              render={({ field }) => (
                <AdminInput
                  readOnly={isDisabled}
                  isError={!!errors.info?.termOfService}
                  errorMsg={errors.info?.termOfService?.message}
                  placeholder={'Paste URL'}
                  {...field}
                />
              )}
            />
          </Box>
        </Grid>
        <Grid size={6} mb={3}>
          <Box width={'100%'}>
            <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
              Project Privacy Policy
            </Typography>
            <Controller
              name='info.privacyPolicy'
              control={control}
              render={({ field }) => (
                <AdminInput
                  readOnly={isDisabled}
                  isError={!!errors?.info?.privacyPolicy}
                  errorMsg={errors.info?.privacyPolicy?.message}
                  placeholder={'Paste URL'}
                  {...field}
                />
              )}
            />
          </Box>
        </Grid>
        <Grid size={12} mb={3}>
          <Box width={'100%'}>
            <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
              Google Analytics Measurement ID
            </Typography>
            <Controller
              name='info.gAnalyticId'
              control={control}
              render={({ field }) => (
                <AdminInput
                  readOnly={isDisabled}
                  rows={4}
                  isError={!!errors?.info?.gAnalyticId}
                  errorMsg={errors.info?.gAnalyticId?.message}
                  placeholder={'Enter Measurement ID'}
                  {...field}
                />
              )}
            />
            <Typography paddingTop={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
              For assistance with setting up Google Analytics and obtaining your Measurement ID, visit{' '}
              <a className={`text-[#1976d2]`} target='_blank' href={urlGoggleSupport}>
                Google Analytics Help Center
              </a>
              {''}.
            </Typography>
          </Box>
        </Grid>
        <Grid size={12} mb={3}>
          <SocialNetworks isDisabled={isDisabled} />
        </Grid>
        <Grid size={12} mb={3}>
          <FAQs isDisabled={isDisabled} />
        </Grid>
      </Grid>
    </StepAccordion>
  );
};

export default GeneralProjectInfo;
